import { useState, useEffect } from 'react';
import { CheckCircle2, BookOpen, AlertTriangle, CheckCircle } from 'lucide-react';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { db, appId } from '@/lib/firebase';

interface TrainingChecklistData {
  onboarding?: {
    readAbout?: boolean;
    readMethodology?: boolean;
    readModerationPlaybooks?: boolean;
    understandCorePrinciple?: boolean;
    reviewSamples?: boolean;
    confirmElectionRules?: boolean;
    signAcknowledgment?: boolean;
  };
  dailyModeration?: {
    evidencePresent?: boolean;
    evidenceMatchesCategory?: boolean;
    descriptionObservational?: boolean;
    noAccusations?: boolean;
    noHateSpeech?: boolean;
    noPrivateData?: boolean;
    correctCategory?: boolean;
    appropriateDisclaimer?: boolean;
  };
  categorySpecific?: {
    antiEpal?: {
      promotionalMaterialVisible?: boolean;
      onGovernmentProject?: boolean;
      noIllegalityClaim?: boolean;
      neutralDescription?: boolean;
    };
    projectDelay?: {
      evidenceShowsSite?: boolean;
      noTheftFraudClaims?: boolean;
      avoidsAbandonedFailed?: boolean;
    };
    serviceFailure?: {
      evidenceSupports?: boolean;
      noPersonalAttacks?: boolean;
      noPunishmentDemands?: boolean;
    };
    complianceGap?: {
      evidenceShowsMissing?: boolean;
      statesWhatMissingOnly?: boolean;
    };
  };
  commentModeration?: {
    addsContext?: boolean;
    asksFactualQuestions?: boolean;
    providesAdditionalEvidence?: boolean;
    noAccusations?: boolean;
    noPoliticalCampaigning?: boolean;
    noHarassment?: boolean;
    noGuiltDeclaration?: boolean;
  };
  communitySignals?: {
    reviewManually?: boolean;
    checkBrigading?: boolean;
    confirmApprovalCriteria?: boolean;
    addInternalNote?: boolean;
  };
  disputeHandling?: {
    noEmotionalReply?: boolean;
    reviewEvidence?: boolean;
    checkModerationHistory?: boolean;
    assessSensitivity?: boolean;
    hideIfRisk?: boolean;
    escalateToRoot?: boolean;
    logInAuditLogs?: boolean;
  };
  electionPeriod?: {
    stricterLanguageReview?: boolean;
    preferHideOverDelete?: boolean;
    requireRootApproval?: boolean;
    ensureDisclaimersVisible?: boolean;
    avoidUrgencyDecisions?: boolean;
  };
  redFlags?: {
    nationalLevelFigures?: boolean;
    viralSocialMedia?: boolean;
    legalThreats?: boolean;
    mediaInquiries?: boolean;
    safetyThreats?: boolean;
  };
  languageSafety?: {
    neverDeclareIllegality?: boolean;
    neverAssignIntent?: boolean;
    neverLabelCorrupt?: boolean;
    neverCallForPunishment?: boolean;
    enforceObserved?: boolean;
    enforceReported?: boolean;
    enforceDocumented?: boolean;
    enforceBasedOnEvidence?: boolean;
  };
  auditAccountability?: {
    actionRecorded?: boolean;
    adminIdRecorded?: boolean;
    timestampRecorded?: boolean;
    reasonStated?: boolean;
  };
}

interface AdminTrainingChecklistProps {
  currentUserId: string;
  currentUserRole: 'root' | 'admin';
}

export const AdminTrainingChecklist = ({
  currentUserId,
  currentUserRole: _currentUserRole,
}: AdminTrainingChecklistProps) => {
  // Suppress unused variable warning - reserved for future role-based features
  void _currentUserRole;
  const [checklist, setChecklist] = useState<TrainingChecklistData>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load checklist from Firestore
  useEffect(() => {
    const loadChecklist = async () => {
      try {
        const adminRef = doc(db, 'artifacts', appId, 'admins', currentUserId);
        const adminSnap = await getDoc(adminRef);
        
        if (adminSnap.exists()) {
          const data = adminSnap.data();
          setChecklist(data.trainingChecklist || {});
        }
      } catch (error) {
        console.error('[AdminTrainingChecklist] Error loading checklist:', error);
      } finally {
        setLoading(false);
      }
    };

    loadChecklist();
  }, [currentUserId]);

  // Save checklist to Firestore
  const saveChecklist = async (updatedChecklist: TrainingChecklistData) => {
    setSaving(true);
    try {
      const adminRef = doc(db, 'artifacts', appId, 'admins', currentUserId);
      await updateDoc(adminRef, {
        trainingChecklist: updatedChecklist,
        updatedAt: serverTimestamp(),
        updatedBy: currentUserId,
      });
      setChecklist(updatedChecklist);
    } catch (error) {
      console.error('[AdminTrainingChecklist] Error saving checklist:', error);
      alert('Failed to save checklist. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Update a specific checklist item
  const updateChecklistItem = (
    section: keyof TrainingChecklistData,
    item: string,
    value: boolean
  ) => {
    const updated = { ...checklist };
    if (!updated[section]) {
      updated[section] = {} as any;
    }
    (updated[section] as any)[item] = value;
    saveChecklist(updated);
  };

  // Update nested category-specific checklist item
  const updateCategoryChecklistItem = (
    category: 'antiEpal' | 'projectDelay' | 'serviceFailure' | 'complianceGap',
    item: string,
    value: boolean
  ) => {
    const updated = { ...checklist };
    if (!updated.categorySpecific) {
      updated.categorySpecific = {};
    }
    if (!updated.categorySpecific[category]) {
      updated.categorySpecific[category] = {};
    }
    (updated.categorySpecific[category] as any)[item] = value;
    saveChecklist(updated);
  };

  // Calculate completion percentages
  const calculateCompletion = (section: keyof TrainingChecklistData): number => {
    const sectionData = checklist[section] as any;
    if (!sectionData) return 0;
    
    const items = Object.keys(sectionData);
    if (items.length === 0) return 0;
    
    const completed = items.filter((key) => sectionData[key] === true).length;
    return Math.round((completed / items.length) * 100);
  };

  // Check if onboarding is complete
  const isOnboardingComplete = () => {
    const completion = calculateCompletion('onboarding');
    return completion === 100;
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading training checklist...</p>
        </div>
      </div>
    );
  }

  const onboardingComplete = isOnboardingComplete();

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 rounded-t-lg">
        <div className="flex items-center gap-3">
          <BookOpen size={28} className="text-blue-100" />
          <div className="flex-1">
            <h1 className="text-2xl font-bold">Admin Training Checklist</h1>
            <p className="text-blue-100 mt-1">
              Complete these checklists to ensure consistent moderation standards
            </p>
          </div>
          {saving && (
            <div className="text-blue-100 text-sm">Saving...</div>
          )}
        </div>
      </div>

      {/* Onboarding Warning */}
      {!onboardingComplete && (
        <div className="bg-yellow-50 border-l-4 border-yellow-600 p-4 m-6 rounded">
          <div className="flex items-start gap-3">
            <AlertTriangle className="text-yellow-600 mt-0.5" size={20} />
            <div>
              <h3 className="font-semibold text-yellow-900 mb-1">
                Onboarding Not Complete
              </h3>
              <p className="text-sm text-yellow-800">
                You must complete all onboarding items before moderating live content. 
                Current progress: {calculateCompletion('onboarding')}%
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="p-6 lg:p-8 space-y-8">
        {/* Section 1: New Admin Onboarding */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              1. New Admin Onboarding Checklist
            </h2>
            <div className="flex items-center gap-2">
              <span className="text-sm text-gray-600">
                {calculateCompletion('onboarding')}% Complete
              </span>
              {onboardingComplete && (
                <CheckCircle className="text-green-600" size={20} />
              )}
            </div>
          </div>
          <p className="text-gray-700 mb-4">
            Complete ALL items before moderating live content:
          </p>
          
          <div className="space-y-3">
            {[
              { key: 'readAbout', label: 'Read /docs/about.md' },
              { key: 'readMethodology', label: 'Read /docs/methodology.md' },
              { key: 'readModerationPlaybooks', label: 'Read /docs/moderation_playbooks.md' },
              { key: 'understandCorePrinciple', label: 'Understand VerifyPH core principle: "We document citizen-submitted evidence, not judge."' },
              { key: 'reviewSamples', label: 'Review sample approved and rejected reports' },
              { key: 'confirmElectionRules', label: 'Confirm understanding of election-period rules' },
              { key: 'signAcknowledgment', label: 'Sign internal acknowledgment: "I understand VerifyPH moderation neutrality rules."' },
            ].map((item) => {
              const checked = checklist.onboarding?.[item.key as keyof typeof checklist.onboarding] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('onboarding', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                  {checked && <CheckCircle2 className="text-green-600 flex-shrink-0" size={20} />}
                </label>
              );
            })}
          </div>

          {!onboardingComplete && (
            <div className="mt-4 bg-red-50 border-l-4 border-red-600 p-3 rounded">
              <p className="text-sm text-red-800 font-semibold">
                🚫 Do NOT moderate until all items are completed.
              </p>
            </div>
          )}
        </section>

        {/* Section 2: Daily Moderation Checklist */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">
              2. Daily Moderation Checklist (Every Session)
            </h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('dailyModeration')}% Complete
            </span>
          </div>
          <p className="text-gray-700 mb-4">
            Before approving ANY report, confirm:
          </p>
          
          <div className="space-y-3">
            {[
              { key: 'evidencePresent', label: 'Evidence is present (photo/video)' },
              { key: 'evidenceMatchesCategory', label: 'Evidence matches the report category' },
              { key: 'descriptionObservational', label: 'Description is observational (what is seen)' },
              { key: 'noAccusations', label: 'No accusations (illegal, corrupt, etc.)' },
              { key: 'noHateSpeech', label: 'No hate speech, harassment, or threats' },
              { key: 'noPrivateData', label: 'No private personal data beyond public view' },
              { key: 'correctCategory', label: 'Correct category selected' },
              { key: 'appropriateDisclaimer', label: 'Appropriate disclaimer will be shown' },
            ].map((item) => {
              const checked = checklist.dailyModeration?.[item.key as keyof typeof checklist.dailyModeration] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('dailyModeration', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-600 p-3 rounded">
            <p className="text-sm text-yellow-800">
              If unsure → <strong>Hide and escalate</strong>.
            </p>
          </div>
        </section>

        {/* Section 3: Category-Specific Quick Checks */}
        <section className="border-b border-gray-200 pb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">
            3. Category-Specific Quick Checks
          </h2>

          {/* Anti-Epal Watch */}
          <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Anti-Epal Watch</h3>
            <div className="space-y-2">
              {[
                { key: 'promotionalMaterialVisible', label: 'Promotional material visible' },
                { key: 'onGovernmentProject', label: 'Appears on government-funded project' },
                { key: 'noIllegalityClaim', label: 'No claim of illegality or intent' },
                { key: 'neutralDescription', label: 'Uses neutral description only' },
              ].map((item) => {
                const checked = checklist.categorySpecific?.antiEpal?.[item.key as keyof typeof checklist.categorySpecific.antiEpal] || false;
                return (
                  <label
                    key={item.key}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => updateCategoryChecklistItem('antiEpal', item.key, e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Project Delay / Ghost Project */}
          <div className="mb-6 bg-orange-50 border border-orange-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Project Delay / Ghost Project</h3>
            <div className="space-y-2">
              {[
                { key: 'evidenceShowsSite', label: 'Evidence shows site condition' },
                { key: 'noTheftFraudClaims', label: 'No claims of theft or fraud' },
                { key: 'avoidsAbandonedFailed', label: 'Language avoids "abandoned" or "failed"' },
              ].map((item) => {
                const checked = checklist.categorySpecific?.projectDelay?.[item.key as keyof typeof checklist.categorySpecific.projectDelay] || false;
                return (
                  <label
                    key={item.key}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => updateCategoryChecklistItem('projectDelay', item.key, e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Service Failure */}
          <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Service Failure</h3>
            <div className="space-y-2">
              {[
                { key: 'evidenceSupports', label: 'Evidence supports observation' },
                { key: 'noPersonalAttacks', label: 'No personal attacks' },
                { key: 'noPunishmentDemands', label: 'No demands for punishment' },
              ].map((item) => {
                const checked = checklist.categorySpecific?.serviceFailure?.[item.key as keyof typeof checklist.categorySpecific.serviceFailure] || false;
                return (
                  <label
                    key={item.key}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => updateCategoryChecklistItem('serviceFailure', item.key, e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>

          {/* Compliance / Transparency Gap */}
          <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
            <h3 className="font-semibold text-gray-900 mb-3">Compliance / Transparency Gap</h3>
            <div className="space-y-2">
              {[
                { key: 'evidenceShowsMissing', label: 'Evidence shows missing signage/info' },
                { key: 'statesWhatMissingOnly', label: 'Description states what is missing only' },
              ].map((item) => {
                const checked = checklist.categorySpecific?.complianceGap?.[item.key as keyof typeof checklist.categorySpecific.complianceGap] || false;
                return (
                  <label
                    key={item.key}
                    className="flex items-start gap-2 text-sm cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={(e) => updateCategoryChecklistItem('complianceGap', item.key, e.target.checked)}
                      className="mt-1 w-4 h-4 text-blue-600 rounded"
                    />
                    <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                      {item.label}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </section>

        {/* Section 4: Comment Moderation */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">4. Comment Moderation Checklist</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('commentModeration')}% Complete
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-3">✅ Approve comments ONLY if:</h3>
              <div className="space-y-2">
                {[
                  { key: 'addsContext', label: 'Adds context or clarification' },
                  { key: 'asksFactualQuestions', label: 'Asks factual questions' },
                  { key: 'providesAdditionalEvidence', label: 'Provides additional evidence' },
                ].map((item) => {
                  const checked = checklist.commentModeration?.[item.key as keyof typeof checklist.commentModeration] || false;
                  return (
                    <label key={item.key} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => updateChecklistItem('commentModeration', item.key, e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded"
                      />
                      <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-3">❌ Hide or remove comments if they:</h3>
              <div className="space-y-2">
                {[
                  { key: 'noAccusations', label: 'Accuse individuals of wrongdoing' },
                  { key: 'noPoliticalCampaigning', label: 'Contain political campaigning' },
                  { key: 'noHarassment', label: 'Encourage harassment or threats' },
                  { key: 'noGuiltDeclaration', label: 'Declare guilt or illegality' },
                ].map((item) => {
                  const checked = checklist.commentModeration?.[item.key as keyof typeof checklist.commentModeration] || false;
                  return (
                    <label key={item.key} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => updateChecklistItem('commentModeration', item.key, e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded"
                      />
                      <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4 bg-blue-50 border-l-4 border-blue-600 p-3 rounded">
            <p className="text-sm text-blue-800">
              <strong>Rule:</strong> Comments must never escalate beyond the original report.
            </p>
          </div>
        </section>

        {/* Section 5: Community Signals */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">5. Community Signals Review Checklist</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('communitySignals')}% Complete
            </span>
          </div>
          <p className="text-gray-700 mb-4">When valid votes or abuse flags increase:</p>
          
          <div className="space-y-3">
            {[
              { key: 'reviewManually', label: 'Review the report manually' },
              { key: 'checkBrigading', label: 'Check for brigading or coordinated behavior' },
              { key: 'confirmApprovalCriteria', label: 'Confirm report still meets approval criteria' },
              { key: 'addInternalNote', label: 'Add internal note if no action is taken' },
            ].map((item) => {
              const checked = checklist.communitySignals?.[item.key as keyof typeof checklist.communitySignals] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('communitySignals', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-red-50 border-l-4 border-red-600 p-3 rounded">
            <p className="text-sm text-red-800 font-semibold">
              🚫 Never auto-hide based on votes alone.
            </p>
          </div>
        </section>

        {/* Section 6: Dispute & Complaint Handling */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">6. Dispute & Complaint Handling Checklist</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('disputeHandling')}% Complete
            </span>
          </div>
          <p className="text-gray-700 mb-4">When a complaint is received (politician, LGU, citizen):</p>
          
          <div className="space-y-3">
            {[
              { key: 'noEmotionalReply', label: 'Do NOT reply emotionally or defensively' },
              { key: 'reviewEvidence', label: 'Review evidence and language' },
              { key: 'checkModerationHistory', label: 'Check moderation history' },
              { key: 'assessSensitivity', label: 'Assess legal or election sensitivity' },
              { key: 'hideIfRisk', label: 'If risk exists: Set report to Hidden' },
              { key: 'escalateToRoot', label: 'If risk exists: Escalate to Root Admin' },
              { key: 'logInAuditLogs', label: 'Log action in auditLogs' },
            ].map((item) => {
              const checked = checklist.disputeHandling?.[item.key as keyof typeof checklist.disputeHandling] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('disputeHandling', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-red-50 border-l-4 border-red-600 p-3 rounded">
            <p className="text-sm text-red-800 font-semibold">
              🚫 Do NOT delete unless ordered or clearly justified.
            </p>
          </div>
        </section>

        {/* Section 7: Election Period */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">7. Election Period Checklist (High-Risk Mode)</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('electionPeriod')}% Complete
            </span>
          </div>
          <p className="text-gray-700 mb-4">During election periods:</p>
          
          <div className="space-y-3">
            {[
              { key: 'stricterLanguageReview', label: 'Apply stricter language review' },
              { key: 'preferHideOverDelete', label: 'Prefer Hide over Delete' },
              { key: 'requireRootApproval', label: 'Require Root Admin approval for: High-profile individuals, Media-linked reports, COMELEC-related issues' },
              { key: 'ensureDisclaimersVisible', label: 'Ensure disclaimers are visible' },
              { key: 'avoidUrgencyDecisions', label: 'Avoid urgency-based decisions' },
            ].map((item) => {
              const checked = checklist.electionPeriod?.[item.key as keyof typeof checklist.electionPeriod] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('electionPeriod', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-blue-50 border-l-4 border-blue-600 p-3 rounded">
            <p className="text-sm text-blue-800">
              <strong>Rule:</strong> Visibility can wait. Legal safety cannot.
            </p>
          </div>
        </section>

        {/* Section 8: Red-Flag Indicators */}
        <section className="border-b border-gray-200 pb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">8. Red-Flag Indicators (Immediate Escalation)</h2>
          <p className="text-gray-700 mb-4">Escalate immediately if a report involves:</p>
          
          <div className="space-y-3">
            {[
              { key: 'nationalLevelFigures', label: 'National-level political figures' },
              { key: 'viralSocialMedia', label: 'Viral social media attention' },
              { key: 'legalThreats', label: 'Legal threats or takedown demands' },
              { key: 'mediaInquiries', label: 'Media inquiries' },
              { key: 'safetyThreats', label: 'Threats to personal safety' },
            ].map((item) => {
              const checked = checklist.redFlags?.[item.key as keyof typeof checklist.redFlags] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('redFlags', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-red-50 border-l-4 border-red-600 p-3 rounded">
            <p className="text-sm text-red-800">
              <strong>Action:</strong> Hide → Escalate → Document
            </p>
          </div>
        </section>

        {/* Section 9: Language Safety */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">9. Language Safety Checklist (Non-Negotiable)</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('languageSafety')}% Complete
            </span>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-red-50 border-l-4 border-red-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-3">Admins must NEVER approve content that:</h3>
              <div className="space-y-2">
                {[
                  { key: 'neverDeclareIllegality', label: 'Declares illegality' },
                  { key: 'neverAssignIntent', label: 'Assigns intent or motive' },
                  { key: 'neverLabelCorrupt', label: 'Labels someone corrupt or guilty' },
                  { key: 'neverCallForPunishment', label: 'Calls for punishment' },
                ].map((item) => {
                  const checked = checklist.languageSafety?.[item.key as keyof typeof checklist.languageSafety] || false;
                  return (
                    <label key={item.key} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => updateChecklistItem('languageSafety', item.key, e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded"
                      />
                      <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>

            <div className="bg-green-50 border-l-4 border-green-600 p-4 rounded">
              <h3 className="font-semibold text-gray-900 mb-3">Admins SHOULD enforce:</h3>
              <div className="space-y-2">
                {[
                  { key: 'enforceObserved', label: '"Observed"' },
                  { key: 'enforceReported', label: '"Reported"' },
                  { key: 'enforceDocumented', label: '"Documented"' },
                  { key: 'enforceBasedOnEvidence', label: '"Based on uploaded evidence"' },
                ].map((item) => {
                  const checked = checklist.languageSafety?.[item.key as keyof typeof checklist.languageSafety] || false;
                  return (
                    <label key={item.key} className="flex items-start gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={(e) => updateChecklistItem('languageSafety', item.key, e.target.checked)}
                        className="mt-1 w-4 h-4 text-blue-600 rounded"
                      />
                      <span className={checked ? 'text-gray-600 line-through' : 'text-gray-900'}>
                        {item.label}
                      </span>
                    </label>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Section 10: Audit & Accountability */}
        <section className="border-b border-gray-200 pb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-bold text-gray-900">10. Audit & Accountability Checklist</h2>
            <span className="text-sm text-gray-600">
              {calculateCompletion('auditAccountability')}% Complete
            </span>
          </div>
          <p className="text-gray-700 mb-4">For every moderation action:</p>
          
          <div className="space-y-3">
            {[
              { key: 'actionRecorded', label: 'Action recorded' },
              { key: 'adminIdRecorded', label: 'Admin ID recorded' },
              { key: 'timestampRecorded', label: 'Timestamp recorded' },
              { key: 'reasonStated', label: 'Reason stated clearly and factually' },
            ].map((item) => {
              const checked = checklist.auditAccountability?.[item.key as keyof typeof checklist.auditAccountability] || false;
              return (
                <label
                  key={item.key}
                  className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={(e) => updateChecklistItem('auditAccountability', item.key, e.target.checked)}
                    className="mt-1 w-5 h-5 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <span className={`flex-1 ${checked ? 'text-gray-700 line-through' : 'text-gray-900'}`}>
                    {item.label}
                  </span>
                </label>
              );
            })}
          </div>

          <div className="mt-4 bg-blue-50 border-l-4 border-blue-600 p-3 rounded">
            <p className="text-sm text-blue-800">
              Audit logs exist to: Protect VerifyPH → Protect admins → Demonstrate neutrality
            </p>
          </div>
        </section>

        {/* Section 11: Final Rule */}
        <section>
          <h2 className="text-xl font-bold text-gray-900 mb-4">11. Final Rule for Admins</h2>
          <div className="bg-yellow-50 border-l-4 border-yellow-600 p-5 rounded">
            <p className="text-gray-900 mb-4">
              If you are unsure, uncomfortable, or pressured:
            </p>
            <div className="space-y-2 mb-4">
              <p className="text-gray-800 font-semibold">→ <strong>Hide the content</strong></p>
              <p className="text-gray-800 font-semibold">→ <strong>Escalate to Root Admin</strong></p>
              <p className="text-gray-800 font-semibold">→ <strong>Document everything</strong></p>
            </div>
            <p className="text-gray-800 font-semibold">
              VerifyPH prioritizes long-term trust over speed.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
};
